import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

/**
 * L'emballage cadeau, coché article par article dans le panier.
 *
 * Le site historique ne facturait pas l'emballage comme un article : il tenait
 * un poste de total à part, `emballage-ttc`, à côté du port, alimenté par une
 * case cochée sur chaque ligne et multiplié par la quantité de cette ligne.
 * Shopify n'a pas ce poste : une commande n'a que des lignes, une livraison,
 * des taxes et des remises. L'équivalent exact est donc **une ligne d'article**,
 * celle du produit d'emballage, dont la quantité suit le nombre d'articles
 * emballés.
 *
 * Une seule ligne pour tout le panier, et non une par article coché : c'est ce
 * que le fichier de commandes envoie déjà à Sylob aujourd'hui, une ligne
 * `DEE000096` avec sa quantité, et c'est ce que l'atelier prépare.
 *
 * L'état vit sur cette ligne, dans la propriété `_pour` : la liste des clés de
 * lignes emballées, que le Liquid lit pour cocher les cases. À côté, `_empreintes`
 * retient ce qu'est chaque article, pour le retrouver quand Shopify change sa clé
 * (voir `empreinte`). Rien n'est écrit sur les lignes d'articles elles-mêmes, ce
 * qui les laisse hors d'atteinte de la fusion opérée par la Cart Transform du
 * configurateur.
 */

const CONFIG = 'akinod-emballage-config';
const MARQUEUR = '_emballage';
const POUR = '_pour';
/** L'empreinte de chaque article emballé, alignée sur les clés de `_pour`. */
const EMPREINTES = '_empreintes';
/** Les sondes d'emballage offert restées sans effet, pour ne pas les rejouer en boucle. */
const SONDES = 'akinod-emballage-sondes';
/** Les libellés posés par ce script, remplacés par ceux de la page au premier appel. */
const LIBELLES = { etUnAutre: 'et 1 autre', etAutres: 'et # autres' };
/**
 * La propriété visible, sans souligné : elle nomme les articles emballés et suit la
 * commande jusqu'au paiement, à l'accusé de réception et au bon de livraison. Sans
 * elle, le client voit « Emballage cadeau, 11,70 € » sans savoir ce qu'il emballe, et
 * l'atelier non plus.
 */
let POUR_VISIBLE = 'Pour';

/**
 * Deux mises à jour du panier peuvent se suivre : on les met à la queue leu leu.
 *
 * Un simple drapeau « occupé » aurait perdu le second clic : cocher deux lignes
 * coup sur coup n'en aurait emballé qu'une, la case restant cochée à l'écran
 * jusqu'au redessin suivant. On enchaîne donc les opérations sur une promesse
 * plutôt que d'en jeter une.
 */
let file = Promise.resolve();

function enfiler(travail) {
  file = file.then(travail, travail);

  return file;
}

/**
 * @returns {{variante: number, titre: string, verifierAuChargement: boolean} | null}
 */
function configuration() {
  const balise = document.getElementById(CONFIG);

  if (!balise) return null;

  try {
    const lue = JSON.parse(balise.textContent || '{}');

    // Sans variante, il n'y a pas de produit d'emballage à poser : on ne fait
    // rien plutôt que d'ajouter un article au hasard.
    if (!lue.variante) return null;

    // Les libellés arrivent rendus dans la langue de la page : un fichier
    // d'`assets/` ne passe pas par Liquid et ne peut pas appeler `t`.
    if (lue.pour) POUR_VISIBLE = lue.pour;
    if (lue.etUnAutre) LIBELLES.etUnAutre = lue.etUnAutre;
    if (lue.etAutres) LIBELLES.etAutres = lue.etAutres;

    return {
      variante: Number(lue.variante),
      titre: lue.titre || 'Emballage cadeau',
      verifierAuChargement: lue.verifierAuChargement === true,
    };
  } catch {
    return null;
  }
}

const racine = () => window.Shopify?.routes?.root ?? '/';

async function panier() {
  const reponse = await fetch(`${racine()}cart.js`, { headers: { Accept: 'application/json' } });

  return reponse.ok ? reponse.json() : null;
}

/** La ligne d'emballage, reconnue à son marqueur et non à sa référence. */
function ligneEmballage(contenu) {
  return contenu.items.find((ligne) => ligne.properties?.[MARQUEUR]);
}

/** Les clés de lignes emballées, telles que la ligne d'emballage les porte. */
function clesEmballees(ligne) {
  const brut = ligne?.properties?.[POUR] ?? '';

  return brut.split(',').map((c) => c.trim()).filter(Boolean);
}

/**
 * L'article, et non la ligne : ce qui reste le même quand Shopify change la clé.
 *
 * La clé d'une ligne n'est pas stable : Shopify la change dès que la remise posée
 * sur la ligne change. Mesuré le 20/09/2026 en production : quand THINKPINK devient
 * applicable, la ligne pochette passe de `…24b9f3ed` à `…585166ff` sans qu'aucune de
 * ses propriétés ne bouge, et une ligne de 2 couverts se coupe en deux, l'une gardant
 * sa clé, l'autre en recevant une neuve. Une réduction « Achetez X, obtenez Y » pose
 * une remise à 0 € sur chaque article compté dans son seuil : un couvert seul, compté
 * en entier, change donc de clé sans que rien ne garde l'ancienne. La ligne
 * d'emballage ne le retrouvait plus : la case se décochait, la pochette partait, et
 * le totebag avec elle quand le panier repassait sous le seuil. Constaté le 08/10.
 *
 * L'empreinte tient à ce qui ne bouge pas : la variante et les propriétés. Les deux
 * moitiés d'une ligne coupée la partagent ; deux lignes ordinaires ne le peuvent pas,
 * Shopify les aurait fusionnées d'office. Une ligne dont `cart.js` ne montre aucune
 * propriété y ajoute son titre : c'est le cas d'une configuration fusionnée (voir le
 * verrou de quantité de `cart-products.liquid`), et deux couverts gravés de même
 * finition ne s'y distinguent que par leur titre composé. Sans lui, cocher l'un
 * emballait aussi l'autre.
 *
 * Elle se calcule toujours depuis `cart.js`, jamais depuis la page : le Liquid et
 * l'API JSON ne voient pas la même chose d'une ligne fusionnée. Les propriétés
 * d'emballage en sont écartées : elles vivent sur la ligne de pochette, jamais sur un
 * article.
 */
function empreinte(ligne) {
  const proprietes = Object.entries(ligne.properties ?? {})
    .filter(([cle]) => cle !== MARQUEUR && cle !== POUR && cle !== POUR_VISIBLE && cle !== EMPREINTES)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  const titre = proprietes.length ? '' : decoder(ligne.title ?? '');

  return hacher(JSON.stringify([ligne.variant_id, proprietes, titre]));
}

/**
 * Une empreinte courte d'un texte (cyrb53, 53 bits) : de quoi distinguer les articles
 * d'un panier, et assez court pour voyager dans une propriété de ligne.
 */
function hacher(texte) {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;

  for (let i = 0; i < texte.length; i += 1) {
    const code = texte.charCodeAt(i);

    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/**
 * Les deux listes de la ligne d'emballage, lues ensemble : la clé, et l'empreinte
 * retenue pour elle. Deux listes de longueurs différentes ne se lisent pas ensemble :
 * c'est une ligne d'emballage posée avant les empreintes, et on n'en garde que les clés.
 */
function entrees(ligne) {
  const cles = (ligne?.properties?.[POUR] ?? '').split(',').map((c) => c.trim());
  const retenues = (ligne?.properties?.[EMPREINTES] ?? '').split(',').map((e) => e.trim());
  const alignees = retenues.length === cles.length;

  return cles
    .map((cle, rang) => ({ cle, retenue: alignees ? retenues[rang] : '' }))
    .filter((entree) => entree.cle);
}

/**
 * Les articles que le client a voulu emballer, par empreinte.
 *
 * Une clé encore au panier donne l'empreinte de sa ligne, recalculée. Une clé que
 * Shopify a remplacée donne l'empreinte retenue à la dernière écriture : c'est elle qui
 * retrouve l'article sous sa nouvelle clé. Une clé partie sans empreinte retenue ne
 * donne rien, comme avant.
 */
function articlesVoulus(contenu, ligne) {
  const presentes = new Map(articles(contenu).map((l) => [l.key, l]));
  const voulus = new Set();

  for (const { cle, retenue } of entrees(ligne)) {
    if (presentes.has(cle)) {
      voulus.add(empreinte(presentes.get(cle)));
    } else if (retenue) {
      voulus.add(retenue);
    }
  }

  return voulus;
}

/** L'article d'une case : sa ligne si elle est au panier, sinon l'empreinte retenue pour sa clé. */
function empreinteDeCle(contenu, ligne, cle) {
  const presente = articles(contenu).find((l) => l.key === cle);

  if (presente) return empreinte(presente);

  return entrees(ligne).find((entree) => entree.cle === cle)?.retenue || null;
}

/**
 * Les lignes à emballer : toutes celles des articles voulus, y compris les moitiés
 * qu'une remise a détachées d'eux. L'ordre du panier est conservé, car c'est lui qui
 * nomme les articles sur la ligne de pochette.
 */
function lignesEmballees(contenu, voulus) {
  return articles(contenu).filter((l) => voulus.has(empreinte(l)));
}

/** La ligne d'emballage telle qu'elle doit être pour ces articles. */
function etatVoulu(contenu, voulus, offre) {
  const emballees = lignesEmballees(contenu, voulus);

  return {
    quantite: emballees.reduce((somme, l) => somme + l.quantity, 0),
    proprietes: {
      [MARQUEUR]: offre.titre,
      [POUR]: emballees.map((l) => l.key).join(','),
      [POUR_VISIBLE]: nommerLesArticles(emballees),
      [EMPREINTES]: emballees.map(empreinte).join(','),
    },
  };
}

/** Vrai si la ligne d'emballage s'écarte de ce qu'elle doit être. */
function aRemettre(ligne, voulu) {
  return ligne.quantity !== voulu.quantite
    || clesEmballees(ligne).join(',') !== voulu.proprietes[POUR]
    || ligne.properties?.[POUR_VISIBLE] !== voulu.proprietes[POUR_VISIBLE]
    || (ligne.properties?.[EMPREINTES] ?? '') !== voulu.proprietes[EMPREINTES];
}

/**
 * Le texte de la propriété visible : les articles emballés, nommés.
 *
 * Le titre d'une configuration est déjà long ; au-delà de deux articles on compte
 * plutôt que d'aligner une phrase que personne ne lit. La quantité est rappelée quand
 * elle dépasse un, car c'est elle qui explique le montant.
 */
/**
 * Le titre d'une ligne arrive échappé pour le HTML : « L&#39;échappée ». Écrit tel quel
 * dans une propriété, il traverse le panier et le paiement sans jamais être redécodé, et
 * le client lit l'entité à l'écran. On la remet en caractère avant de l'enregistrer.
 */
function decoder(texte) {
  const zone = document.createElement('textarea');
  zone.innerHTML = texte;

  return zone.value;
}

function nommerLesArticles(lignes) {
  // `title` et non `product_title` : deux couverts 12:34 ne se distinguent que par
  // leur motif et leur gravure, et c'est justement ce qu'il faut lire au paiement.
  const noms = lignes.map((l) => decoder(l.quantity > 1 ? `${l.quantity} × ${l.title}` : l.title));

  if (noms.length <= 2) return noms.join(', ');

  const reste = noms.length - 2;
  // La règle de pluriel n'est pas la même partout — l'allemand ne prend pas de
  // « s » — donc les deux formes viennent des fichiers de langue, pas d'un
  // ternaire sur le nombre.
  const suite = reste === 1 ? LIBELLES.etUnAutre : LIBELLES.etAutres.replace('#', reste);

  return `${noms.slice(0, 2).join(', ')} ${suite}`;
}

/**
 * Les sections de panier présentes sur la page, et le composant qui porte chacune.
 *
 * Shopify sait rendre une section au moment où il modifie le panier : c'est ainsi que
 * le thème rafraîchit ses lignes, son récapitulatif et son total sans recharger.
 *
 * On garde le composant, et pas seulement son identifiant, parce qu'il faut savoir si
 * la section vit dans le tiroir : le tiroir se remet à jour en « hydratation », la page
 * en « plein ». C'est la distinction que fait `component-cart-items.js` avec `isDrawer`.
 */
function sectionsDuPanier() {
  return [...document.querySelectorAll('cart-items-component')]
    .filter((n) => n.dataset.sectionId)
    .map((n) => ({ id: n.dataset.sectionId, tiroir: Boolean(n.closest('theme-drawer, cart-drawer-component')) }));
}

/**
 * Fait scintiller le total en attendant la réponse.
 *
 * Le thème le fait sur ses propres changements de quantité ; sans ça, cocher la case
 * laissait deux montants figés à l'écran pendant l'aller-retour, et le récapitulatif
 * paraissait ne pas réagir.
 */
function fairePatienterLesTotaux() {
  for (const noeud of document.querySelectorAll('[ref="cartTotal"], .ak-totaux__emballage text-component')) {
    if (typeof noeud.shimmer === 'function') noeud.shimmer();
  }
}

async function poster(chemin, corps) {
  const sections = sectionsDuPanier();
  // `sections_url` : sans lui, Shopify rend la section dans le contexte de la racine
  // du site et non celui du panier. Le thème l'envoie sur tous ses appels ; l'omettre
  // rendait un récapitulatif calculé hors de la page qu'on regarde.
  const enrichi = sections.length
    ? { ...corps, sections: sections.map((s) => s.id).join(','), sections_url: window.location.pathname }
    : corps;
  const reponse = await fetch(`${racine()}${chemin}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(enrichi),
  });

  if (!reponse.ok) return null;

  try {
    const lu = await reponse.json();

    return { ...lu, ak_sections: sections };
  } catch {
    return null;
  }
}

/**
 * Redessine le panier avec le HTML que Shopify vient de rendre.
 *
 * C'est LE point qui manquait. L'événement `CartLinesUpdateEvent` prévient la pastille
 * de l'en-tête et le tiroir, mais **personne ne remet la page du panier à jour** : dans
 * ce thème, c'est le composant qui déclenche le changement qui appelle lui-même
 * `morphSection` (voir `component-cart-items.js`). Notre case à cocher n'étant pas ce
 * composant, le récapitulatif de droite gardait le montant du rendu précédent : six
 * couverts emballés, 19,50 € affichés au lieu de 23,40 €. Le total, lui, semblait
 * bouger — il ne bougeait qu'au changement suivant. Constaté le 07/09.
 */
async function redessiner(resultat) {
  const sections = resultat?.ak_sections ?? [];
  const rendus = resultat?.sections ?? {};

  await Promise.all(sections.map(async ({ id, tiroir }) => {
    const html = rendus[id];

    if (!html) return;

    try {
      await morphSection(id, html, { mode: tiroir ? 'hydration' : 'full' });
    } catch {
      // Une section absente de la réponse ne doit pas interrompre les autres.
    }
  }));
}

/**
 * Prévient le thème, dans sa propre langue.
 *
 * Ses composants n'écoutent pas un simple `CustomEvent` : ils attendent l'événement de
 * Shopify, porteur d'une promesse qui leur livre le HTML des sections. Un événement nu
 * était bien reçu, et n'entraînait aucun redessin.
 *
 * @param {{id: string, quantity: number}} ligne  la ligne d'emballage telle qu'elle
 *   vient d'être posée : le SDK refuse un événement sans aucune ligne.
 */
function prevenirLeTheme(sections, action, ligne, compte) {
  const differe = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(new CartLinesUpdateEvent({
    action,
    context: 'cart',
    // Une ligne complète, avec son identifiant de variante au format long : le SDK
    // d'événements de Shopify valide la charge utile, et se plaignait de deux choses
    // — un tableau vide (« Too small: expected array to have >=1 items »), puis un
    // `merchandiseId` absent. Rien n'était visible à l'écran, seulement en console,
    // mais c'est cette charge que reçoit l'analytique du panier.
    lines: [ligne],
    promise: differe.promise,
    source: 'akinod-emballage',
  }));

  // `itemCount` : c'est lui que lisent la pastille de l'en-tête et le compteur du
  // thème. Sans lui, ils gardaient leur compte d'avant — quatre articles dans le
  // panier, deux affichés.
  differe.resolve({ detail: { sections, itemCount: compte, source: 'akinod-emballage' } });
}

/**
 * Remet la ligne d'emballage d'aplomb sur le panier que Shopify vient de rendre.
 *
 * Changer le nombre de pochettes peut, sous une réduction qui les compte dans son
 * seuil, déplacer la remise d'un article à l'autre : Shopify change alors la clé de
 * ces articles dans la même réponse, et la liste qu'on vient d'écrire est déjà
 * périmée. La case se décochait sous les yeux du client qui venait de la cocher. On
 * réécrit la liste sur les nouvelles clés, sans changer le nombre de pochettes ; deux
 * passes au plus, la seconde ne déplaçant plus aucune remise.
 *
 * Seulement quand la ligne d'emballage est d'un seul tenant : coupée en deux par une
 * remise, la réécrire risquerait de compter des pochettes en double. On la laisse
 * alors comme avant.
 */
async function remettreDAplomb(resultat, voulus, offre) {
  let dernier = resultat;

  for (let passe = 0; passe < 2; passe += 1) {
    // `cart/add.js` ne rend que les lignes ajoutées : on relit alors le panier entier.
    const etat = typeof dernier?.item_count === 'number' ? dernier : await panier();
    const posees = (etat?.items ?? []).filter((l) => l.properties?.[MARQUEUR]);

    if (posees.length !== 1) return dernier;

    const voulu = etatVoulu(etat, voulus, offre);

    if (voulu.quantite === 0 || !aRemettre(posees[0], voulu)) return dernier;

    const suite = await poster('cart/change.js', {
      id: posees[0].key,
      quantity: voulu.quantite,
      properties: voulu.proprietes,
    });

    if (!suite) return dernier;

    dernier = suite;
  }

  return dernier;
}

/**
 * Aligne la ligne d'emballage sur l'ensemble d'articles voulu.
 *
 * Les articles qui ne sont plus au panier sont écartés : un article retiré emporte
 * son emballage, sans quoi le client paierait une pochette pour un couvert qu'il
 * n'achète plus.
 *
 * `calculer` reçoit le panier fraîchement lu, les articles voulus jusqu'ici (par
 * empreinte) et la ligne d'emballage, et rend ceux que l'on veut. Le panier n'est donc
 * lu qu'une fois par opération : c'est ce qui rapproche le rafraîchissement de
 * l'emballage de celui du total, qui paraissaient décorrélés tant qu'il fallait deux
 * allers-retours pour les obtenir.
 *
 * @param {(contenu: any, actuels: Set<string>, ligne: any) => Set<string>} calculer
 */
function appliquer(calculer) {
  const offre = configuration();

  if (!offre) return Promise.resolve();

  return enfiler(async () => {
    try {
      const contenu = await panier();

      if (!contenu) return;

      const ligne = ligneEmballage(contenu);
      const voulus = calculer(contenu, articlesVoulus(contenu, ligne), ligne);
      const voulu = etatVoulu(contenu, voulus, offre);
      const quantite = voulu.quantite;
      const proprietes = voulu.proprietes;

      let resultat = null;
      let action = 'update';

      // On sait ici qu'un appel va partir : les deux montants se mettent à scintiller
      // ensemble, et ils se figeront ensemble. C'est ce qui supprime l'impression de
      // décalage, en plus du redessin ci-dessous qui les rend simultanément justes.
      if (quantite !== 0 || ligne) fairePatienterLesTotaux();

      if (quantite === 0) {
        // Plus rien d'emballé : la ligne disparaît, ou il n'y avait rien à faire.
        if (!ligne) return;

        resultat = await poster('cart/change.js', { id: ligne.key, quantity: 0 });
        action = 'remove';
      } else if (!ligne) {
        resultat = await poster('cart/add.js', {
          items: [{ id: offre.variante, quantity: quantite, properties: proprietes }],
        });
        action = 'add';
      } else if (aRemettre(ligne, voulu)) {
        resultat = await poster('cart/change.js', {
          id: ligne.key,
          quantity: quantite,
          properties: proprietes,
        });
      } else {
        // Rien n'a bougé : surtout ne pas réémettre l'événement, ce qui ferait
        // tourner le script en rond.
        return;
      }

      // Les pochettes posées, la remise a pu changer des clés : on rattrape la liste
      // avant de redessiner, pour ne jamais montrer une case décochée à tort.
      if (resultat && action !== 'remove') resultat = await remettreDAplomb(resultat, voulus, offre);

      // Le redessin d'abord, l'événement ensuite : la page du panier porte les deux
      // montants, et c'est elle qu'il faut voir juste. L'événement ne sert plus qu'à la
      // pastille de l'en-tête et au tiroir, qui se rafraîchissent seuls.
      await redessiner(resultat);

      // La clé de la ligne d'emballage : celle qu'on vient de créer pour un ajout,
      // celle qu'on avait pour une modification ou un retrait. Après un rattrapage la
      // réponse est le panier entier, où la pochette n'est plus forcément la première.
      const cle = action === 'add'
        ? ((resultat?.items && ligneEmballage(resultat)?.key) ?? '')
        : (ligne?.key ?? '');

      // `cart/change.js` renvoie le panier entier, `cart/add.js` seulement les
      // lignes ajoutées : dans ce dernier cas le compte se déduit de ce qu'on
      // vient de poser.
      const compte = typeof resultat?.item_count === 'number'
        ? resultat.item_count
        : contenu.item_count + quantite;

      prevenirLeTheme(
        resultat?.sections,
        action,
        {
          id: cle,
          quantity: action === 'remove' ? 0 : quantite,
          merchandiseId: `gid://shopify/ProductVariant/${offre.variante}`,
        },
        compte,
      );
    } catch {
      // Un emballage qui ne s'ajoute pas ne doit jamais empêcher d'acheter.
    }
  });
}

/**
 * La case a été cochée ou décochée sur une ligne. C'est l'ARTICLE qui est emballé ou
 * non, avec les moitiés qu'une remise a pu en détacher : décocher l'une décoche
 * l'article, au lieu d'être aussitôt recochée par l'autre.
 */
function basculer(cle, cochee) {
  return appliquer((contenu, actuels, ligne) => {
    const article = empreinteDeCle(contenu, ligne, cle);

    // Une case dont la ligne n'est ni au panier ni dans la mémoire de l'emballage : on
    // ne devine pas, le redessin suivant la remettra dans son état réel.
    if (!article) return actuels;

    const voulus = new Set(actuels);

    if (cochee) {
      voulus.add(article);
    } else {
      voulus.delete(article);
    }

    return voulus;
  });
}

/**
 * Le panier a changé sans nous : quantité modifiée, article retiré, code promo.
 * On ne change pas d'avis sur ce qui est emballé, on remet seulement la
 * quantité et la liste d'aplomb, sur les clés que Shopify vient de donner.
 */
function resynchroniser() {
  return appliquer((contenu, actuels) => actuels);
}

/* ─────────────────────────────────────────────────────────────────────────────
 * L'emballage offert par un code.
 *
 * Le site historique portait, sur ses bons et promotions, une case « Emballage
 * cadeau offert » (`actions.emballage_cadeau_gratuit`). Tant que le code est
 * appliqué, l'emballage est gratuit ET coché d'office sur tous les articles du
 * panier concernés par le code (`classes/Promo.class.php`), sans condition de
 * fiche : le client ne peut pas le décocher, la case se recoche au passage
 * suivant. Deux bons l'ont utilisé, BC-AUBOGA et BC-MEEDAI, en octobre 2024.
 *
 * Dans Shopify, l'équivalent est un code « 100 % sur le produit Emballage
 * cadeau ». Mais un tel code ne s'applique qu'à une ligne d'emballage déjà
 * présente : saisi sur un panier sans pochette, Shopify le refuse comme
 * inapplicable, et ne le garde pas.
 *
 * On le vérifie donc par l'effet, sans liste de codes à tenir : quand un code
 * est refusé, on emballe tous les articles, on repropose les codes, et on garde
 * si Shopify accepte le code et met la pochette à zéro. Sinon on rend au client
 * exactement ce qu'il avait coché, et on retient l'essai pour ne pas le rejouer.
 * Une fois l'offre active, chaque article ajouté ou décoché est réemballé.
 * ───────────────────────────────────────────────────────────────────────────── */

/** La pochette est offerte : elle avait un prix, il ne reste rien à payer. */
function offreActive(ligne) {
  return Boolean(ligne) && ligne.original_line_price > 0 && ligne.final_line_price === 0;
}

/**
 * Les articles qui affichent la case d'emballage sur la page, par empreinte, ou rien si
 * la page ne montre pas le panier : sans la liste sous les yeux, on ne sait pas qui y
 * a droit.
 *
 * Rien non plus si la page ne montre pas CE panier. Dessinée avant que Shopify ne
 * change des clés, elle porte encore les anciennes, et juger sur elles retirait la
 * pochette d'articles qui y avaient droit. Quand une case désigne une ligne absente du
 * panier, ou qu'un article du panier n'a pas sa rangée à l'écran, on ne juge pas : le
 * passage suivant le fera sur une page à jour.
 */
function empreintesEligibles(contenu) {
  const cases = [...document.querySelectorAll('[data-akinod-emballage][data-cle]')];

  if (!cases.length) return null;

  const presentes = new Map(articles(contenu).map((l) => [l.key, l]));
  const rangees = new Set(
    [...document.querySelectorAll('cart-items-component [data-key]')].map((r) => r.dataset.key),
  );

  if (cases.some((c) => !presentes.has(c.dataset.cle))) return null;
  if ([...presentes.keys()].some((cle) => !rangees.has(cle))) return null;

  return new Set(cases.map((c) => empreinte(presentes.get(c.dataset.cle))));
}

/**
 * Les articles du panier, c'est-à-dire tout sauf la ligne d'emballage, et sauf le
 * produit offert par un code (`akinod-cadeau.js`) : il n'a pas été choisi, un code qui
 * emballe tout le panier ne l'emballe pas.
 */
function articles(contenu) {
  return contenu.items.filter((l) => !l.properties?.[MARQUEUR] && !l.properties?._cadeau_offert);
}

/**
 * Les codes qui offrent un produit plutôt que l'emballage : `akinod-cadeau.js` s'en
 * occupe. Sans cette exclusion, les deux scripts sondaient le même code refusé en même
 * temps, et celui-ci le retirait du panier pendant que l'autre posait le cadeau.
 */
function codesDeCadeau() {
  try {
    const code = JSON.parse(document.getElementById('akinod-cadeau-config')?.textContent || '{}').code;

    return code ? [String(code).toUpperCase()] : [];
  } catch {
    return [];
  }
}

function sondesSansEffet() {
  try {
    return new Set(JSON.parse(window.sessionStorage.getItem(SONDES) || '[]'));
  } catch {
    return new Set();
  }
}

function retenirSondeSansEffet(signature) {
  try {
    const deja = sondesSansEffet();

    deja.add(signature);
    window.sessionStorage.setItem(SONDES, JSON.stringify([...deja].slice(-20)));
  } catch {
    // Sans stockage, on rejouera la sonde : c'est plus lent, jamais faux.
  }
}

/**
 * Remet le message « code non applicable » du thème, que le redessin du panier a masqué.
 * Mêmes éléments que `cart-discount.js` : le conteneur d'erreur et son texte de code.
 */
function signalerCodeRefuse() {
  for (const bloc of document.querySelectorAll('cart-discount-component')) {
    bloc.querySelector('[ref="cartDiscountError"]')?.classList.remove('hidden');
    bloc.querySelector('[ref="cartDiscountErrorDiscountCode"]')?.classList.remove('hidden');
  }
}

let verification = null;

/**
 * Emballe tout le panier si un code offre l'emballage, et seulement dans ce cas.
 *
 * @param {{codes: string[], refuses: string[]} | null} saisie  les codes que le
 *   client vient de proposer, et ceux que Shopify a refusés ; sans saisie, on
 *   regarde les codes que le panier porte déjà.
 */
function verifierOffre(saisie = null) {
  if (verification || !configuration()) return verification;

  verification = (async () => {
    try {
      await file;

      const contenu = await panier();

      if (!contenu) return;

      const ligne = ligneEmballage(contenu);
      const produits = articles(contenu);
      const choisies = articlesVoulus(contenu, ligne);
      const toutes = new Set(produits.map(empreinte));
      const toutEmballe = produits.every((l) => choisies.has(empreinte(l)));

      if (produits.length === 0) return;

      // L'offre est déjà là : on emballe ce qui ne l'est pas encore, comme le
      // legacy recochait la case.
      if (offreActive(ligne)) {
        if (!toutEmballe) await appliquer(() => toutes);

        return;
      }

      const codes = saisie?.codes ?? (contenu.discount_codes ?? []).map((c) => String(c.code));
      const cadeaux = codesDeCadeau();
      const refuses = (saisie?.refuses
        ?? (contenu.discount_codes ?? []).filter((c) => c && c.applicable === false).map((c) => String(c.code)))
        .filter((c) => !cadeaux.includes(c.toUpperCase()));

      if (refuses.length === 0) {
        // Plus d'offre, plus de code à essayer : un article qui n'affiche pas la case
        // ne garde pas de pochette. Le site historique la laissait, facturée, sur un
        // lunch bag que le client ne pouvait plus décocher ; c'est le seul écart voulu.
        const eligibles = empreintesEligibles(contenu);

        if (eligibles && [...choisies].some((article) => toutes.has(article) && !eligibles.has(article))) {
          await appliquer(() => new Set([...choisies].filter((article) => eligibles.has(article))));
        }

        return;
      }

      const signature = JSON.stringify([
        [...refuses].map((c) => c.toUpperCase()).sort(),
        produits.map((l) => `${l.key}:${l.quantity}`).sort(),
      ]);

      if (sondesSansEffet().has(signature)) return;

      if (!toutEmballe) await appliquer(() => toutes);

      // Les codes reproposés avec la pochette au panier : c'est la seule façon de
      // savoir ce qu'ils offrent, Shopify ne gardant pas un code refusé.
      const reponse = await poster('cart/update.js', { discount: codes.join(',') });
      const accepte = (reponse?.discount_codes ?? [])
        .some((c) => c.applicable === true && refuses.some((r) => r.toUpperCase() === String(c.code).toUpperCase()));

      if (accepte && offreActive(ligneEmballage(reponse))) {
        await redessiner(reponse);

        return;
      }

      // Le code offrait autre chose : on rend au client ce qu'il avait coché, et le
      // panier tel que Shopify l'aurait laissé.
      retenirSondeSansEffet(signature);

      if (saisie) {
        // Une saisie refusée ne reste pas au panier, comme dans le parcours natif :
        // la reproposer l'y avait accrochée, et elle ressortirait au paiement.
        const gardes = codes.filter((code) => !refuses.some((r) => r.toUpperCase() === code.toUpperCase()));

        await redessiner(await poster('cart/update.js', { discount: gardes.join(',') }));
      }

      if (!toutEmballe) await appliquer(() => choisies);

      // Le redessin a effacé le message du thème : le client doit lire que son code
      // ne s'applique pas.
      if (saisie) signalerCodeRefuse();
    } catch {
      // Une vérification qui échoue ne doit jamais empêcher d'acheter.
    } finally {
      verification = null;
    }
  })();

  return verification;
}

// Écoute déléguée : le panier se redessine à chaque changement, les cases
// d'origine disparaissent avec lui.
document.addEventListener('change', async (evenement) => {
  const case_ = evenement.target.closest?.('[data-akinod-emballage]');

  if (!case_) return;

  await basculer(case_.dataset.cle, case_.checked);

  // Décocher un emballage offert : le site historique le recochait.
  verifierOffre();
});

// Un code vient d'être saisi dans le panier : s'il est refusé, il offre peut-être
// l'emballage. On attend la réponse de Shopify, qui dit lesquels il refuse.
document.addEventListener(StandardEvents.cartDiscountUpdate, async (evenement) => {
  let resultat = null;

  try {
    resultat = await evenement.promise;
  } catch {
    return;
  }

  const codes = (evenement.discountCodes ?? []).map((c) => String(c.code));
  const refuses = (resultat?.cart?.discountCodes ?? [])
    .filter((c) => c.applicable === false)
    .map((c) => String(c.code));

  // Un code posé ou retiré déplace des remises, donc des clés : on remet la liste
  // d'aplomb. Sauf quand Shopify vient de refuser le code du cadeau : `akinod-cadeau.js`
  // pose alors le totebag et repropose le code, et son événement nous rappellera sur le
  // panier définitif. Passer avant lui ferait redessiner deux fois, dans le désordre.
  const cadeaux = codesDeCadeau();

  if (!refuses.some((c) => cadeaux.includes(c.toUpperCase()))) resynchroniser();

  verifierOffre({ codes, refuses });
});

// Un panier qui arrive avec un code déjà posé, par un lien `/discount/CODE`
// ou d'une visite précédente. Sur la page du panier seulement. La remise a pu
// changer des clés en l'absence du client : la ligne d'emballage est remise
// d'aplomb d'abord, et `verifierOffre` attend qu'elle le soit.
if (configuration()?.verifierAuChargement) {
  resynchroniser();
  verifierOffre();
}

/**
 * Le panier a changé sans nous : on remet l'emballage d'aplomb, une fois le
 * changement RÉELLEMENT enregistré.
 *
 * Le thème émet cet événement **avant** son aller-retour serveur, et lui joint une
 * promesse qui se dénoue à la réponse. Se resynchroniser à la réception lisait donc
 * l'état d'avant : passer un couvert de 2 à 3 laissait l'emballage à 2, et le
 * récapitulatif affichait 7,80 € pour trois pochettes. Constaté le 07/09. On attend
 * la promesse quand elle existe, et on se contente de l'événement sinon.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, async (evenement) => {
  // On ignore l'événement qu'on vient d'émettre soi-même. `source` est posé sur
  // l'événement à l'émission, et se retrouve dans `detail` à la résolution : les
  // deux sont regardés, sans quoi le script se rappellerait lui-même.
  if ((evenement.source ?? evenement.detail?.source) === 'akinod-emballage') return;

  try {
    await evenement.promise;
  } catch {
    // Un changement qui a échoué n'a rien modifié : il n'y a rien à resynchroniser.
    return;
  }

  await resynchroniser();

  // Un article ajouté pendant qu'un code offre l'emballage est emballé à son tour.
  verifierOffre();
});
